import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import type {
  GetFilteredVehiclesResponse,
  // TODO: Uncomment this once the OneGraph migration has been applied
  // GetRenderImageUrlsResponse,
  VehicleDetailsOutput,
} from '../types';
import { GET_FILTERED_VEHICLES, VEHICLE_DETAILS } from '../schemas/VehicleDetailSchema';
import { QUERY_VERSION } from '../constants';
import { vdpPageLoad } from './analyticsEventListeners';

// TODO: Uncomment this once the OneGraph migration has been applied
// import { GET_RENDER_IMAGE_URLS_BY_CONFIGURATION } from '../schemas/RenderImageUrlsSchema';
// import { BRAND_CODE, IMAGES_CONFIGURATION, IMAGES_TYPE } from './constants';

type GetVehicleDetailsProps = (
  vehicleId: string | null,
  market: string | null,
) => VehicleDetailsOutput;

const GetVehicleDetails: GetVehicleDetailsProps = (vehicleId, market) => {
  const { i18n } = useTranslation();
  const lang = i18n.languages[0] || 'en';

  const isValidVehicleId = useMemo(() => {
    return vehicleId?.length && vehicleId.length > 17 && vehicleId.endsWith('=');
  }, [vehicleId]);

  // TODO: Uncomment this once the OneGraph migration has been applied
  // const [getRenderImageUrls, { data: renderImageUrlsData, loading: loadingRenderImagesUrl }] =
  //   useLazyQuery<GetRenderImageUrlsResponse>(GET_RENDER_IMAGE_URLS_BY_CONFIGURATION, {
  //     errorPolicy: 'all',
  //   });

  const [getVehicleDetails, { data: vehicleDetailsData, loading: loadingDetails }] =
    useLazyQuery<GetFilteredVehiclesResponse>(VEHICLE_DETAILS, {
      errorPolicy: 'all',

      // TODO: Uncomment this once the OneGraph migration has been applied.
      // This function will allow you to retrieve the images once the stockCar query returns the data.

      // onCompleted: (data: GetFilteredVehiclesResponse) => {
      //   const modelCode = data.stockCar.model.id.code;
      //   const modelYear = data.stockCar.model.id.year;
      //   const exteriorColor = data.stockCar.colorInfo.exteriorColor.colorInfo.code;
      //   getRenderImageUrls({
      //     variables: {
      //       input: {
      //         modelRequests: {
      //           exteriorColorCode: `${exteriorColor}${exteriorColor}`,
      //           modelCode,
      //           modelYear,
      //           mimeType: IMAGES_TYPE,
      //         },
      //         client: proccess.env.MEDIA_SERVICE_CLIENT_ID,
      //         brand: BRAND_CODE[market?.toLowerCase() as keyof typeof BRAND_CODE],
      //         configs: IMAGES_CONFIGURATION,
      //       },
      //     },
      //   }).catch((error: unknown) => {
      //     console.error('function getRenderImageUrls -> ERROR:', error);
      //   });
      // },
    });

  const [getFilteredVehicles, { loading: loadingFiltered }] = useLazyQuery(GET_FILTERED_VEHICLES, {
    errorPolicy: 'all',
    onCompleted: (data: GetFilteredVehiclesResponse) => {
      const vehicle = data.getFilteredVehiclesForWormwood?.vehicles?.[0];
      const totalCount = data.getFilteredVehiclesForWormwood?.filterResults?.totalCount ?? 0;
      const searchedVehicleId = vehicle?.id;
      // If total count is more than 2 then something is wrong so skip
      if (vehicleId && searchedVehicleId && totalCount < 5 && totalCount !== 0) {
        getVehicleDetails({
          variables: {
            version: QUERY_VERSION,
            market: market?.toUpperCase(),
            lang,
            id: searchedVehicleId,
          },
        }).catch((error: unknown) => {
          console.error('Uncaught error:', error);
        });
      }
    },
  });

  // To split when we need JDP data, and when DDC
  // ------------------------------------------------------------------------------------
  const importDb = useMemo(() => {
    const isUsMarket = market === 'us' || market === 'usuc';

    return isUsMarket ? 'AGC_USA_JDP' : 'AGC_CAN_JDP'; // CA OR US T3 and source JDP
  }, [market]);

  useEffect(() => {
    if (!vehicleId || !market) return;
    if (isValidVehicleId) {
      getVehicleDetails({
        variables: {
          version: QUERY_VERSION,
          market: market.toUpperCase(),
          lang,
          id: vehicleId,
        },
      }).catch((error: unknown) => {
        console.error('Uncaught error:', error);
      });
      return;
    }
    getFilteredVehicles({
      variables: {
        version: QUERY_VERSION,
        market: [market.toUpperCase()],
        lang: 'en',
        filters: `t_vin.${vehicleId}`,
        ranges: '',
        // we don't need the actual vehicle list in the filter drawer,
        // so we set limit=0 in this case to speed up the process
        limit: 1,
        offset: 0,
        preset: `stat-import.${importDb}`,
      },
    }).catch((error: unknown) => {
      console.error('Uncaught error:', error);
    });
  }, [getFilteredVehicles, getVehicleDetails, importDb, isValidVehicleId, lang, market, vehicleId]);

  const vehicleData = vehicleDetailsData?.getVehicleInfoForWormwood;

  // TODO: Uncomment this once the OneGraph migration has been applied to get the images data
  // const vehicleImageData =
  //   renderImageUrlsData?.renderImageUrlsByConfiguration.renderImages[0].result;

  // TODO: Since we are fetching the image URLs from another query, we should also include the loading flag here as loadingRenderImagesUrl
  const loading = loadingDetails || loadingFiltered;

  if (vehicleData) {
    const {
      modelName,
      bodyType,
      modelPrice,
      modelYear,
      modelCode,
      dealerName,
      saleOrderType,
      vehicleOrderStatus,
      vin,
      exteriorColor,
      upholsteryColor,
      stockNumber,
      trimName,
      gearType,
      driveType,
      kvpsSyncId,
      vehicleType,
      isAvailableNow,
      vehicleInventoryType,
    } = vehicleData;

    const vdpAnalytics = {
      modelName: modelName.slice(5),
      bodyType,
      price: modelPrice,
      dealerName,
      modelYear,
      modelCode,
      // change later - when non audi vehicles are available
      make: 'Audi',
      saleType:
        vehicleType === 'new' && isAvailableNow
          ? 'available-now'
          : saleOrderType || vehicleInventoryType,
      status: vehicleType === 'new' && isAvailableNow ? null : vehicleOrderStatus || 'Unavailable',
      vin,
      exteriorColor,
      upholsteryColor,
      stockNumber,
      trim: trimName,
      gear: gearType,
      drivetrain: driveType,
      inventoryType: vehicleType,
      dealerCode: kvpsSyncId,
      orderType: saleOrderType === 'SOLD - Customer Sold Order' ? 'Reserved' : null,
    };
    vdpPageLoad(vdpAnalytics);
  }

  // TODO: We should return the vehicleImageData to get the array containing the image URLs
  return { loading, vehicleData, market };
};

export default GetVehicleDetails;
